/**
 * Pure helpers for settings-inheritance wording.
 * Source: design-system/components/setting-row.md ("SettingOrigin wording" table) and
 * knowledge/contracts/settings.md (resolution mapping → guild → global → default).
 */

/** `origin[key]` from the resolver, plus "mixed" for bulk selections that disagree. */
export type SettingOriginValue = "default" | "global" | "guild" | "mapping" | "mixed";
/** The level being edited. */
export type SettingLevel = "global" | "guild" | "mapping";
/** Visual treatment. "overridden" = value is set at the level being edited. */
export type SettingOriginKind = "default" | "inherited" | "guild" | "overridden" | "mixed";

const RANK: Record<Exclude<SettingOriginValue, "mixed">, number> = {
  default: 0,
  global: 1,
  guild: 2,
  mapping: 3,
};
const LEVEL_RANK: Record<SettingLevel, number> = { global: 1, guild: 2, mapping: 3 };

export function settingOriginKind(
  origin: SettingOriginValue,
  level: SettingLevel = "mapping",
): SettingOriginKind {
  if (origin === "mixed") return "mixed";
  // A value from a deeper level than the one viewed cannot normally happen; treat as set here.
  if (RANK[origin] >= LEVEL_RANK[level]) return "overridden";
  if (origin === "guild") return "guild";
  if (level === "global") return "default";
  return "inherited";
}

/** Badge text, exactly per the wording table. */
export function settingOriginLabel(
  origin: SettingOriginValue,
  level: SettingLevel = "mapping",
): string {
  const kind = settingOriginKind(origin, level);
  switch (kind) {
    case "mixed":
      return "mixed";
    case "default":
      return "default";
    case "guild":
      return "inherited · guild";
    case "overridden":
      return level === "global" ? "set" : "overridden";
    default:
      return "inherited";
  }
}

/** Longer sentence for tooltips and screen readers. */
export function settingOriginDescription(
  origin: SettingOriginValue,
  level: SettingLevel = "mapping",
): string {
  switch (settingOriginKind(origin, level)) {
    case "mixed":
      return "Selected items have different values";
    case "overridden":
      return level === "global" ? "Set in global settings" : `Set on this ${level}`;
    case "guild":
      return "Inherited from the source guild";
    case "default":
      return "Built-in default";
    default:
      return origin === "global"
        ? "Inherited from global settings"
        : "Inherited from the built-in default";
  }
}

/** Reset is offered only when the value is set at the level being edited. */
export function canResetSetting(origin: SettingOriginValue, level: SettingLevel): boolean {
  return settingOriginKind(origin, level) === "overridden";
}
